import React, { useState } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  Button, 
  Chip,
  Box,
  Skeleton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import PDFPreview from './PDFPreview';

function DocumentGrid({ documents, onAddToBasket, loading }) {
  const [previewDoc, setPreviewDoc] = useState(null);
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Card className="glass-dark">
              <CardContent>
                <Skeleton variant="text" width="80%" height={30} />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (documents.length === 0) {
    return (
      <Box 
        className="glass-dark rounded-2xl p-12 text-center"
        sx={{ minHeight: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      >
        <DescriptionIcon sx={{ fontSize: 80, color: 'rgba(0, 150, 136, 0.3)', mx: 'auto', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enter a university portal URL to start discovering exam papers
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {documents.map((doc) => (
          <Grid item xs={12} sm={6} key={doc.id}>
            <Card className="glass-dark rounded-xl hover:scale-105 transition-transform duration-200">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <DescriptionIcon sx={{ color: '#009688', fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div" sx={{ mb: 1, wordBreak: 'break-word' }}>
                      {doc.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {doc.year && (
                        <Chip 
                          icon={<CalendarTodayIcon />}
                          label={doc.year} 
                          size="small" 
                          sx={{ bgcolor: 'rgba(0, 150, 136, 0.2)' }}
                        />
                      )}
                      {doc.unitCode && (
                        <Chip 
                          icon={<SchoolIcon />}
                          label={doc.unitCode} 
                          size="small" 
                          sx={{ bgcolor: 'rgba(0, 150, 136, 0.2)' }}
                        />
                      )}
                      {doc.semester && (
                        <Chip 
                          label={doc.semester} 
                          size="small" 
                          sx={{ bgcolor: 'rgba(0, 150, 136, 0.2)' }}
                        />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      Type: {doc.type}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button 
                  size="small" 
                  startIcon={<VisibilityIcon />}
                  onClick={() => setPreviewDoc(doc)}
                  sx={{ color: '#009688' }}
                >
                  Preview
                </Button>
                <Button 
                  size="small" 
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => onAddToBasket(doc)}
                  sx={{ 
                    ml: 'auto',
                    bgcolor: '#009688',
                    '&:hover': {
                      bgcolor: '#00796b',
                    },
                  }}
                >
                  Add
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {previewDoc && (
        <PDFPreview
          open={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          documentUrl={previewDoc.url}
          title={previewDoc.title}
        />
      )}
    </>
  );
}

export default DocumentGrid;
